import React, { useState, useEffect } from 'react';
import { useServerConfig } from '../context/ServerConfigContext';
import './MenuManagement.css';

const MenuManagement = () => {
  const { config, isConfigLoaded, saveConfig } = useServerConfig();
  const [menuSettings, setMenuSettings] = useState({});
  const [statusMessage, setStatusMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // 사용자 등급 정의
  const userGrades = [
    {
      id: 'guest',
      name: '게스트',
      level: 'Guest',
      icon: '👤',
      description: '비로그인 사용자'
    },
    {
      id: 'bronze',
      name: '브론즈',
      level: 'Level 1',
      icon: '🥉',
      description: '기본 사용자'
    },
    {
      id: 'silver',
      name: '실버',
      level: 'Level 2',
      icon: '🥈',
      description: '일반 사용자'
    },
    {
      id: 'gold',
      name: '골드',
      level: 'Level 3',
      icon: '🥇',
      description: '우수 사용자'
    },
    {
      id: 'platinum',
      name: '플래티넘',
      level: 'Level 4',
      icon: '💎',
      description: '프리미엄 사용자'
    },
    {
      id: 'diamond',
      name: '다이아몬드',
      level: 'Level 5',
      icon: '💠',
      description: '최고급 사용자'
    }
  ];

  // 사용 가능한 메뉴 목록
  const availableMenus = [
    {
      id: 'board',
      name: '게시판',
      icon: '📋',
      description: '커뮤니티 게시판 시스템',
      path: '/board',
      category: 'main'
    },
    {
      id: 'admin',
      name: '관리자 페이지',
      icon: '⚙️',
      description: '시스템 관리 및 설정',
      path: '/admin',
      category: 'admin'
    }
  ];

  // 기본 메뉴 권한 설정
  const defaultMenuSettings = {
    guest: ['board'], // 게스트는 게시판만 접근 가능
    bronze: ['board'],
    silver: ['board'],
    gold: ['board'],
    platinum: ['board'],
    diamond: ['board']
  };

  useEffect(() => {
    if (isConfigLoaded) {
      loadMenuSettings();
    }
  }, [isConfigLoaded, config]);

  // config.menuPermissions가 변경될 때도 다시 로드
  useEffect(() => {
    if (config && config.menuPermissions) {
      console.log('🔄 [MenuManagement] config.menuPermissions 변경 감지, 다시 로드');
      loadMenuSettings();
    }
  }, [config?.menuPermissions]);

  const loadMenuSettings = () => {
    try {
      console.log('🔧 [MenuManagement] 메뉴 설정 로드 시작:', {
        hasConfig: !!config,
        hasMenuPermissions: !!(config && config.menuPermissions),
        menuPermissions: config?.menuPermissions
      });

      // app-config.json에서 메뉴 권한 설정 로드
      if (config && config.menuPermissions) {
        console.log('✅ [MenuManagement] app-config.json에서 메뉴 설정 로드:', config.menuPermissions);
        setMenuSettings(config.menuPermissions);
      } else {
        console.log('📋 [MenuManagement] config.menuPermissions 없음, 기본 메뉴 설정 사용');
        setMenuSettings(defaultMenuSettings);
      }
    } catch (error) {
      console.error('❌ [MenuManagement] 메뉴 설정 로드 실패:', error);
      setMenuSettings(defaultMenuSettings);
    }
  };

  const saveMenuSettings = async () => {
    setIsLoading(true);
    try {
      // app-config.json에 메뉴 설정 저장
      const updatedConfig = {
        ...config,
        menuPermissions: menuSettings,
        timestamp: new Date().toISOString(),
        lastSaved: new Date().toLocaleString('ko-KR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      };

      console.log('💾 [MenuManagement] 저장할 데이터:', {
        hasConfig: !!config,
        menuSettings,
        updatedConfig: Object.keys(updatedConfig),
        menuPermissionsInConfig: updatedConfig.menuPermissions
      });

      const result = await saveConfig(updatedConfig);
      console.log('✅ [MenuManagement] saveConfig 결과:', result);

      setStatusMessage({ type: 'success', text: '메뉴 설정이 성공적으로 저장되었습니다.' });
      setTimeout(() => setStatusMessage(null), 3000);
    } catch (error) {
      console.error('❌ [MenuManagement] 메뉴 설정 저장 실패:', error);
      setStatusMessage({ type: 'error', text: '메뉴 설정 저장에 실패했습니다.' });
      setTimeout(() => setStatusMessage(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMenu = (gradeId, menuId) => {
    setMenuSettings(prev => {
      const gradeMenus = prev[gradeId] || [];
      const newGradeMenus = gradeMenus.includes(menuId)
        ? gradeMenus.filter(id => id !== menuId)
        : [...gradeMenus, menuId];

      return {
        ...prev,
        [gradeId]: newGradeMenus
      };
    });
  };

  const enableAllMenus = (gradeId) => {
    setMenuSettings(prev => ({
      ...prev,
      [gradeId]: availableMenus.map(menu => menu.id)
    }));
  };

  const disableAllMenus = (gradeId) => {
    setMenuSettings(prev => ({
      ...prev,
      [gradeId]: []
    }));
  };

  const resetToDefault = (gradeId) => {
    setMenuSettings(prev => ({
      ...prev,
      [gradeId]: defaultMenuSettings[gradeId] || []
    }));
  };

  const getEnabledMenuCount = (gradeId) => {
    return (menuSettings[gradeId] || []).length;
  };

  const isMenuEnabled = (gradeId, menuId) => {
    return (menuSettings[gradeId] || []).includes(menuId);
  };

  return (
    <div className="menu-management">
      {/* Header */}
      <div className="menu-header">
        <div>
          <h2 className="menu-title">메뉴 권한 관리</h2>
          <p className="menu-subtitle">사용자 등급별로 접근 가능한 메뉴를 설정하세요.</p>
        </div>
        <button
          onClick={saveMenuSettings}
          disabled={isLoading}
          className="save-btn"
        >
          {isLoading ? (
            <>
              <span>⏳</span>
              저장 중...
            </>
          ) : (
            <>
              <span>💾</span>
              설정 저장
            </>
          )}
        </button>
      </div>

      {/* Status Message */}
      {statusMessage && (
        <div className={`status-message ${statusMessage.type}`}>
          <span>{statusMessage.type === 'success' ? '✅' : '❌'}</span>
          {statusMessage.text}
        </div>
      )}

      {/* Grade Sections */}
      <div className="grade-sections">
        {userGrades.map(grade => (
          <div key={grade.id} className="grade-section">
            <div className={`grade-header ${grade.id}`}>
              <div className="grade-info">
                <span className="grade-icon">{grade.icon}</span>
                <div>
                  <div className="grade-name">{grade.name}</div>
                  <div className="grade-level">{grade.level} - {grade.description}</div>
                </div>
              </div>
              <div className="menu-count">
                {getEnabledMenuCount(grade.id)}개 메뉴 활성화
              </div>
            </div>

            <div className="menu-content">
              {/* Permission Panel */}
              <div className="permission-panel">
                <div className="permission-title">
                  <span>🔧</span>
                  일괄 설정
                </div>
                <div className="permission-actions">
                  <button
                    onClick={() => enableAllMenus(grade.id)}
                    className="permission-btn enable-all-btn"
                  >
                    전체 활성화
                  </button>
                  <button
                    onClick={() => disableAllMenus(grade.id)}
                    className="permission-btn disable-all-btn"
                  >
                    전체 비활성화
                  </button>
                  <button
                    onClick={() => resetToDefault(grade.id)}
                    className="permission-btn reset-btn"
                  >
                    기본값으로 복원
                  </button>
                </div>
              </div>

              {/* Menu List */}
              <div className="menu-list">
                {availableMenus.map(menu => (
                  <div
                    key={menu.id}
                    className={`menu-item ${isMenuEnabled(grade.id, menu.id) ? 'enabled' : ''}`}
                    onClick={() => toggleMenu(grade.id, menu.id)}
                  >
                    <div className="menu-item-header">
                      <div className="menu-item-info">
                        <span className="menu-item-icon">{menu.icon}</span>
                        <span className="menu-item-name">{menu.name}</span>
                      </div>
                      <button
                        className={`menu-toggle ${isMenuEnabled(grade.id, menu.id) ? 'enabled' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMenu(grade.id, menu.id);
                        }}
                      >
                        <div className="menu-toggle-slider"></div>
                      </button>
                    </div>
                    <div className="menu-item-description">
                      {menu.description}
                    </div>
                    <div className="menu-item-path">
                      {menu.path}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MenuManagement;